import type { LedgerEntry } from "@/types/ledger";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useManagerEvents } from "@/hooks/useManagerEvents";
import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

function toNumber(value: string | number | undefined | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === "number") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    year: "2-digit",
  }).format(date);
}

type Props = {
  entries: LedgerEntry[];
  onPost: (entryId: string) => Promise<void> | void;
  postingId: string | null;
  loading?: boolean;
};

export function LedgerEntriesTable({ entries, onPost, postingId, loading }: Props) {
  const { events: managerEvents } = useManagerEvents(200);
  const eventMap = useMemo(() => Object.fromEntries(managerEvents.map((event) => [event.id, formatEventLabel(event)])), [managerEvents]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleEntry = (entryId: string) => {
    setExpandedId((current) => (current === entryId ? null : entryId));
  };

  return (
    <div className="rounded-2xl border border-border bg-background/30 shadow-inner shadow-black/40">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Journal entries</h2>
          <p className="text-xs text-muted-foreground">Latest 100 entries for the active artist scope.</p>
        </div>
      </div>
      <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-background/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-3 text-left font-medium text-muted-foreground"> </th>
              <th className="px-4 py-3 text-left font-medium">Date</th>
              <th className="px-4 py-3 text-left font-medium">Journal / Memo</th>
              <th className="px-4 py-3 text-left font-medium">Source</th>
              <th className="px-4 py-3 text-right font-medium">Amount</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  Loading ledger entries…
                </td>
              </tr>
            )}
            {!loading && entries.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  No entries have been recorded yet. Create your first journal entry to get started.
                </td>
              </tr>
            )}
            {!loading &&
              entries.map((entry) => {
                const isExpanded = expandedId === entry.id;
                return (
                  <Fragment key={entry.id}>
                    <tr className="border-t border-border">
                      <td className="align-top px-3 py-4">
                        <button
                          type="button"
                          onClick={() => toggleEntry(entry.id)}
                          className="rounded-full border border-border/70 bg-background/40 p-1 text-foreground/80 hover:bg-muted"
                          aria-label={isExpanded ? "Collapse entry" : "Expand entry"}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="align-top px-4 py-4 text-sm text-foreground/80 whitespace-nowrap">{formatDate(entry.date)}</td>
                      <td className="align-top px-4 py-4">
                        <div className="font-medium text-foreground">
                          {entry.journal?.name ?? `Journal #${entry.journal_id}`}
                        </div>
                        <p className="text-xs text-muted-foreground">{entry.memo || "—"}</p>
                      </td>
                      <td className="align-top px-4 py-4 text-sm text-foreground/70">{entry.source || "—"}</td>
                      <td className="align-top px-4 py-4 text-right font-semibold text-primary-foreground dark:text-amber-200">
                        {currencyFormatter.format(toNumber(entry.total_debits))}
                      </td>
                      <td className="align-top px-4 py-4">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold",
                            entry.status === "posted"
                              ? "bg-emerald-400/15 text-emerald-200"
                              : "bg-amber-400/15 text-primary-foreground dark:text-amber-200",
                          )}
                        >
                          {entry.status === "posted" ? "Posted" : "Draft"}
                        </span>
                      </td>
                      <td className="align-top px-4 py-4 text-right">
                        {entry.status === "draft" ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={postingId === entry.id}
                            onClick={() => onPost(entry.id)}
                          >
                            {postingId === entry.id ? "Posting…" : "Post entry"}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-t border-border bg-background/40">
                        <td colSpan={7} className="px-6 py-4 text-xs text-foreground/80">
                          <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Lines</div>
                          <div className="space-y-2">
                            {entry.lines.map((line) => (
                              <div
                                key={line.id}
                                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/40 px-3 py-2"
                              >
                                <div className="flex-1 min-w-[160px]">
                                  <div className="font-mono text-[11px] text-muted-foreground">
                                    {line.account?.code ?? line.account_id}
                                  </div>
                                  <div className="text-sm text-foreground">
                                    {line.account?.name ?? "Unassigned"}
                                    {line.memo && <span className="ml-2 text-xs text-muted-foreground">· {line.memo}</span>}
                                  </div>
                                  {line.event_id && (
                                    <div className="mt-1 inline-flex rounded-full bg-muted/60 px-2 py-0.5 text-[10px] uppercase tracking-wide text-foreground/70">
                                      {eventMap[line.event_id] ?? line.event_id}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right font-semibold text-emerald-300 min-w-[120px]">
                                  {line.debit && toNumber(line.debit) > 0
                                    ? `${currencyFormatter.format(toNumber(line.debit))} dr`
                                    : `${currencyFormatter.format(toNumber(line.credit))} cr`}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type ManagerEvent = {
  id: string
  name?: string
  event_date?: string
  city?: string
  state?: string
};

function formatEventLabel(event: ManagerEvent): string {
  const date = event.event_date ? new Date(event.event_date).toLocaleDateString() : "";
  const location = [event.city, event.state].filter(Boolean).join(", ");
  if (event.name) {
    return `${date ? `${date} · ` : ""}${event.name}${location ? ` (${location})` : ""}`;
  }
  return `${date} ${location}`.trim() || event.id;
}
